import React from "react";
import { Route, Routes } from "react-router-dom";
import { useAppSelector } from "../app/hooks";
import { selectSessionToken } from "../features/access-control/accountSlice";
import AuthChecker from "../features/access-control/AuthChecker";
import { AccessLevel } from "../interfaces/accessLevel.enum";
import { IRouteConfig } from "../interfaces/routeConfig.interface";
import Navbar from "../features/navbar/Navbar";

// Import your page components
import LoginPage from "../pages/login-page/LoginPage";
import BusinessesPage from "../pages/BusinessesPage";
import MFASetup from "../features/access-control/MFASetup";
import LandingPage from "../pages/landing-page/LandingPage";
import NotificationsPage from "../pages/notifications-page/NotificationsPage";
import ReportsPage from "../pages/reports-page/ReportsPage";
import PartnersPage from "../pages/partners-page/PartnersPage";
import PartnerDealsPage from "../pages/partner-deals-page/PartnerDealsPage";
import PartnerBusinessesPage from "../pages/partner-businesses-page/PartnerBusinessesPage";
import TerminalsPage from "../pages/terminals-page/TerminalsPage";
import TerminalsCouplingPage from "../pages/terminals-coupling-page/TerminalsCoupingPage";
import PppLinksPage from "../pages/ppp-links-page/PppLinksPage";
import FeatureFlagsPage from "../pages/feature-flags-page/FeatureFlagsPage";
import SecurityPage from "../pages/security-page/SecurityPage";
// import NfcProductsCouplingPage from "../pages/nfc-products-coupling-page/NfcProductsCouplingPage";
import TransactionsPage from "../pages/transactions-page/TransactionsPage";
import TransactionTagsPage from "../pages/transaction-tags-page/TransactionTagsPage";
import TransactionTagsEditorPage from "../pages/transaction-tags-editor-page/TransactionTagsEditorPage";
import CatalogsPage from "../pages/catalogs-page/CatalogsPage";
import CatalogEditorPage from "../pages/catalog-editor-page/CatalogEditorPage";
import BucketDealsPage from "../pages/bucket-deals-page/BucketDealsPage";
import ChatBotPage from "../pages/chat-bot-page/ChatBotPage";
import SalesPage from "../pages/sales-page/SalesPage";
import AdminsPage from "../pages/admins-page/AdminsPage";
import DevelopmentPage from "../pages/development-page/DevelopmentPage";
import QaToolsPage from "../pages/qa-tools-page/QaToolsPage";
import CrossMerchantAnalyticsPage from "../pages/cross-merchant-analytics-page/CrossMerchantAnalyticsPage";
import HospitalityAnalyticsPage from "../pages/hospitality-analytics-page/HospitalityAnalyticsPage";
import LiveTransactionMapPage from "../pages/live-transaction-map-page/LiveTransactionMapPage";
import PosSimulatorPage from "../pages/pos-simulator-page/PosSimulatorPage";
import MarketingSegmentsPage from "../pages/marketing-segments-page/MarketingSegmentsPage";
import PricingToolPage from "../pages/pricing-tool-page/PricingToolPage";
import ReleaseNotesPage from "../pages/release-notes-page/ReleaseNotesPage";
import EmailTemplatesPage from "../pages/email-templates-page/EmailTemplatesPage";
import PaymentHealthPage from "../pages/payment-health-page/PaymentHealthPage";

export const routes: IRouteConfig[] = [
  {
    path: "/",
    component: <LoginPage />,
    isProtected: false,
    accessLevel: [AccessLevel.KlearlyAdmin],
  },
  {
    path: "/mfaSetup",
    component: <MFASetup />,
    isProtected: false,
    accessLevel: [AccessLevel.KlearlyAdmin],
  },
  {
    path: "/home",
    component: <LandingPage />,
    isProtected: true,
    accessLevel: [
      AccessLevel.KlearlyAdmin,
      AccessLevel.CoreTechEmployee,
      AccessLevel.Partner,
    ],
  },
  {
    path: "/businesses",
    label: "Businesses",
    component: <BusinessesPage />,
    isProtected: true,
    accessLevel: [AccessLevel.KlearlyAdmin],
  },
  {
    path: "/reports",
    label: "Report Creator",
    component: <ReportsPage />,
    isProtected: true,
    accessLevel: [AccessLevel.KlearlyAdmin],
  },
  {
    path: "/partners",
    label: "Partners",
    isProtected: true,
    accessLevel: [
      AccessLevel.KlearlyAdmin,
      AccessLevel.KlearlySuperAdmin,
      AccessLevel.KlearlySalesSuperAdmin,
      AccessLevel.KlearlySalesAdmin,
    ],
    isGroup: true,
    children: [
      {
        path: "/partners/list",
        label: "Partners List",
        component: <PartnersPage />,
        isProtected: true,
        accessLevel: [AccessLevel.KlearlyAdmin],
      },
      {
        path: "/partners/deals",
        label: "Partner Deals",
        component: <PartnerDealsPage />,
        isProtected: true,
        accessLevel: [AccessLevel.KlearlyAdmin],
      },
    ],
  },
  {
    path: "/partners/merchants",
    label: "Partner Merchants",
    isProtected: true,
    accessLevel: [AccessLevel.Partner],
    isGroup: true,
    children: [
      {
        path: "/partners/merchants/deals",
        label: "New Merchants",
        component: <PartnerDealsPage />,
        isProtected: true,
        accessLevel: [AccessLevel.Partner],
      },
      {
        path: "/partners/merchants/overview",
        label: "Merchants Overview",
        component: <PartnerBusinessesPage />,
        isProtected: true,
        accessLevel: [AccessLevel.Partner],
      },
    ],
  },
  {
    path: "/terminals",
    label: "Terminals",
    isProtected: true,
    accessLevel: [AccessLevel.KlearlyAdmin, AccessLevel.CoreTechEmployee],
    isGroup: true,
    children: [
      {
        path: "/terminals/list",
        label: "Terminals List",
        component: <TerminalsPage />,
        isProtected: true,
        accessLevel: [AccessLevel.KlearlyAdmin, AccessLevel.CoreTechEmployee],
      },
      {
        path: "/terminals/coupling",
        label: "Terminals Coupling",
        component: <TerminalsCouplingPage />,
        isProtected: true,
        accessLevel: [AccessLevel.KlearlyAdmin, AccessLevel.CoreTechEmployee],
      },
    ],
  },

  // {
  //   path: "/transactions",
  //   label: "Transactions",
  //   isProtected: true,
  //   accessLevel: [AccessLevel.KlearlyAdmin],
  //   isGroup: true,
  //   children: [
  //     {
  //       path: "/transactions/list",
  //       label: "Transactions List",
  //       component: <TransactionsPage />,
  //       isProtected: true,
  //       accessLevel: [AccessLevel.KlearlyAdmin],
  //     },
  //     {
  //       path: "/transactions/tags",
  //       label: "Transaction Tags",
  //       component: <TransactionTagsPage />,
  //       isProtected: true,
  //       accessLevel: [AccessLevel.KlearlyAdmin],
  //     },
  //     {
  //       path: "/transactions/tags/editor",
  //       label: "Tags Editor",
  //       component: <TransactionTagsEditorPage />,
  //       isProtected: true,
  //       accessLevel: [AccessLevel.KlearlyAdmin],
  //     },
  //   ],
  // },
  // {
  //   path: "/catalogs",
  //   label: "Catalogs",
  //   isProtected: true,
  //   accessLevel: [AccessLevel.KlearlyAdmin],
  //   isGroup: true,
  //   children: [
  //     {
  //       path: "/catalogs/list",
  //       label: "Catalogs List",
  //       component: <CatalogsPage />,
  //       isProtected: true,
  //       accessLevel: [AccessLevel.KlearlyAdmin],
  //     },
  //     {
  //       path: "/catalogs/editor",
  //       label: "Catalog Editor",
  //       component: <CatalogEditorPage />,
  //       isProtected: true,
  //       accessLevel: [AccessLevel.KlearlyAdmin],
  //     },
  //   ],
  // },
  // {
  //   path: "/chat-bot",
  //   label: "Chat Bot",
  //   component: <ChatBotPage />,
  //   isProtected: true,
  //   accessLevel: [AccessLevel.KlearlySuperAdmin],
  // },
  {
    path: "/development",
    label: "Development",
    component: <DevelopmentPage />,
    isProtected: true,
    accessLevel: [AccessLevel.KlearlySuperAdmin],
  },
  // Dev-only fixtures. Hidden entirely on prod rather than shown and refused,
  // and the endpoints behind it reject a prod stage regardless.
  ...(process.env.REACT_APP_STAGE !== "prod"
    ? [
        {
          path: "/qa-tools",
          label: "QA Tools",
          component: <QaToolsPage />,
          isProtected: true,
          accessLevel: [AccessLevel.KlearlySuperAdmin],
        },
      ]
    : []),
  {
    path: "/sales",
    label: "Sales",
    component: <SalesPage />,
    isProtected: true,
    accessLevel: [AccessLevel.KlearlyAdmin],
  },
  {
    path: "/release-notes",
    label: "Release Notes",
    component: <ReleaseNotesPage />,
    isProtected: true,
    accessLevel: [AccessLevel.KlearlyAdmin, AccessLevel.KlearlySuperAdmin],
  },
  {
    path: "/email-templates",
    label: "Email Templates",
    component: <EmailTemplatesPage />,
    isProtected: true,
    accessLevel: [AccessLevel.KlearlyAdmin, AccessLevel.KlearlySuperAdmin],
  },
  {
    path: "/tools",
    label: "Tools",
    isProtected: true,
    accessLevel: [AccessLevel.KlearlyAdmin, AccessLevel.KlearlySuperAdmin],
    isGroup: true,
    children: [
      {
        path: "/notifications",
        label: "Notifications",
        component: <NotificationsPage />,
        isProtected: true,
        accessLevel: [AccessLevel.KlearlyAdmin],
      },
      {
        path: "/ppp-links",
        label: "PPP Links",
        component: <PppLinksPage />,
        isProtected: true,
        accessLevel: [AccessLevel.KlearlySuperAdmin],
      },
      {
        path: "/feature-flags",
        label: "Feature Flags",
        component: <FeatureFlagsPage />,
        isProtected: true,
        accessLevel: [AccessLevel.KlearlySuperAdmin],
      },
      {
        path: "/bucket-deals",
        label: "Bucket Deals",
        component: <BucketDealsPage />,
        isProtected: true,
        accessLevel: [AccessLevel.KlearlyAdmin],
      },
      {
        path: "/marketing-segments",
        label: "Marketing Segments",
        component: <MarketingSegmentsPage />,
        isProtected: true,
        accessLevel: [AccessLevel.KlearlyAdmin],
      },
    ],
  },
  {
    path: "/admins",
    label: "Admins",
    component: <AdminsPage />,
    isProtected: true,
    accessLevel: [AccessLevel.KlearlySuperAdmin],
  },
  {
    path: "/payment-health",
    label: "Status Page",
    component: <PaymentHealthPage />,
    isProtected: true,
    accessLevel: [
      AccessLevel.KlearlySuperAdmin,
      AccessLevel.KlearlyAdmin,
      AccessLevel.KlearlyAnalytics,
      AccessLevel.CoreTechEmployee,
    ],
  },
  {
    path: "/pricing-tool",
    label: "Pricing Tool",
    component: <PricingToolPage />,
    isProtected: true,
    accessLevel: [
      AccessLevel.KlearlySuperAdmin,
      AccessLevel.KlearlyAnalytics,
      AccessLevel.KlearlySalesSuperAdmin,
      AccessLevel.KlearlySalesAdmin,
    ],
  },
  {
    path: "/cross-merchant-analytics",
    // No label - hidden from menu
    component: <CrossMerchantAnalyticsPage />,
    isProtected: true,
    accessLevel: [
      // Note: Backend only allows SuperAdmin and specialized roles
      AccessLevel.KlearlySuperAdmin,
      AccessLevel.KlearlyAnalytics,
      AccessLevel.KlearlySalesSuperAdmin,
      AccessLevel.KlearlySalesAdmin,
    ],
  },
  {
    path: "/hospitality-analytics",
    // No label - hidden from menu
    component: <HospitalityAnalyticsPage />,
    isProtected: true,
    accessLevel: [AccessLevel.KlearlySuperAdmin],
  },
  ...(process.env.REACT_APP_STAGE !== "prod"
    ? [
        {
          path: "/live-transaction-map",
          label: "Live Transaction Map",
          component: <LiveTransactionMapPage />,
          isProtected: true,
          accessLevel: [
            AccessLevel.KlearlySuperAdmin,
            AccessLevel.KlearlyAnalytics,
            AccessLevel.KlearlySalesSuperAdmin,
            AccessLevel.KlearlySalesAdmin,
          ],
        },
        {
          path: "/pos-simulator",
          label: "POS Simulator",
          component: <PosSimulatorPage />,
          isProtected: true,
          accessLevel: [AccessLevel.KlearlySuperAdmin],
        },
      ]
    : []),
];

const AppRoutes: React.FC = () => {
  const isAuthenticated = useAppSelector(selectSessionToken);

  return (
    <>
      <Navbar routes={routes} />
      <Routes>
        {routes.map((route) => {
          if (route.children) {
            return route.children.map((childRoute) => (
              <Route
                key={childRoute.path}
                path={childRoute.path}
                element={
                  childRoute.isProtected ? (
                    <AuthChecker
                      accessLevel={childRoute.accessLevel || []}
                      path={childRoute.path}
                    >
                      {childRoute.component}
                    </AuthChecker>
                  ) : (
                    childRoute.component
                  )
                }
              />
            ));
          }
          return (
            <Route
              key={route.path}
              path={route.path}
              element={
                route.isProtected ? (
                  <AuthChecker
                    accessLevel={route.accessLevel || []}
                    path={route.path}
                  >
                    {route.component}
                  </AuthChecker>
                ) : (
                  route.component
                )
              }
            />
          );
        })}
        <Route
          path="/security"
          element={
            <AuthChecker
              accessLevel={[
                AccessLevel.KlearlyAdmin,
                AccessLevel.KlearlySuperAdmin,
                AccessLevel.KlearlyAnalytics,
                AccessLevel.KlearlySalesSuperAdmin,
                AccessLevel.KlearlySalesAdmin,
                AccessLevel.CoreTechEmployee,
                AccessLevel.Partner,
              ]}
              path="/security"
            >
              <SecurityPage />
            </AuthChecker>
          }
        />
      </Routes>
    </>
  );
};

export default AppRoutes;
